import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

export interface StaffInfo {
  id: string;
  fullName: string;
  email?: string;
  phone?: string;
  role?: string;
  department?: string;
  designation?: string;
}

export interface Task {
  id: string;
  taskName: string;
  activityId: string;
  patientName: string;
  patientId: string;
  patientUuid: string;
  ward: string;
  bedRoom: string;
  assignedTo: string;
  completedBy?: string;
  completionTime?: string;
  timeTakenMinutes?: number;
  status: "pending" | "in-progress" | "completed" | "skipped";
  timestamp?: string;
  elapsedTime: string;
  elapsedMinutes: number;
  workflowStep?: string;
  previousStep?: string;
  displayOrder: number;
  // Enhanced staff tracking
  assignedToStaff?: StaffInfo;
  completedByStaff?: StaffInfo;
  assignedAt?: string;
  startedAt?: string;
  // TAT fields
  allowedTatMinutes?: number;
  level1ThresholdMinutes?: number;
  level2ThresholdMinutes?: number;
  level1RemainingMinutes?: number;
  level2RemainingMinutes?: number;
  gracePeriodMinutes?: number;
  tatStatus?: 'green' | 'yellow' | 'red' | 'exceeded';
  isDelayed: boolean;
  patientTotalElapsedTime?: string;
  patientAdmissionDateTime?: string;
}

const parseElapsedTime = (elapsed: string | null): number => {
  if (!elapsed) return 0;
  const parts = elapsed.split(':');
  if (parts.length === 3) {
    // HH:MM:SS format
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  }
  // Fallback for MM:SS format
  const minutes = parseInt(parts[0]) || 0;
  return minutes;
};

export const useTasks = (userId?: string) => {
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();
  return useQuery({
    queryKey: ['tasks', organizationId, currentBranchId, userId],
    queryFn: async () => {
      if (currentBranchId == null || !organizationId) return [];
      // 1) Only consider patients where MD (Mark for Discharge) is completed.
      const { data: mdCompleted, error: mdError } = await supabase
        .from('activities')
        .select('patient_id')
        .eq('workflow_step', 'MD')
        .eq('status', 'completed')
        .eq('branch_id', currentBranchId)
        .eq('organization_id', organizationId);

      if (mdError) throw mdError;

      const mdPatientIds = Array.from(
        new Set((mdCompleted || []).map((a: any) => a.patient_id).filter(Boolean))
      );

      if (mdPatientIds.length === 0) {
        return [];
      }

      // 2) Query activities with patient data only (no profile joins) for those MD-completed patients.
      //    Chunk IDs to avoid oversized request URLs (414 errors).
      const chunkSize = 100;
      const idChunks: string[][] = [];
      for (let i = 0; i < mdPatientIds.length; i += chunkSize) {
        idChunks.push(mdPatientIds.slice(i, i + chunkSize));
      }

      const aggregated: any[] = [];
      for (const ids of idChunks) {
        let query = supabase
          .from('activities')
          .select('*, patients!inner(*)')
          .in('patient_id', ids)
          .eq('branch_id', currentBranchId)
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });

        if (userId) {
          query = query.eq('assigned_to', userId);
        }

        const { data, error } = await query;
        if (error) throw error;
        if (data?.length) {
          aggregated.push(...data);
        }
      }

      const data = aggregated.sort((a, b) => {
        const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
        const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;
        return bTime - aTime;
      });
      
      // Transform to Task objects with TAT calculations
      const tasks: Task[] = await Promise.all(
        (data || []).map(async (activity: any) => {
          const patient = activity.patients;
          const completedByProfile = activity.completed_by_profile;
          
          // Calculate elapsed time from assigned_at for TAT tracking
          let elapsedTime = activity.elapsed_time || '00:00:00';
          if (activity.status === 'in-progress') {
            // Use assigned_at for TAT tracking, fallback to started_at for backwards compatibility
            const startTime = activity.assigned_at || activity.started_at;
            if (startTime) {
              // Convert to IST and calculate elapsed time
              const now = new Date();
              const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
              const istNow = new Date(now.getTime() + istOffset);
              const startTimeDate = new Date(startTime);
              const istStartTime = new Date(startTimeDate.getTime() + istOffset);
              
              const diffMs = istNow.getTime() - istStartTime.getTime();
              const totalSeconds = Math.floor(diffMs / 1000);
              const hours = Math.floor(totalSeconds / 3600);
              const minutes = Math.floor((totalSeconds % 3600) / 60);
              const seconds = totalSeconds % 60;
              
              elapsedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            }
          } else if (activity.elapsed_time) {
            // Keep stored HH:MM:SS format
            elapsedTime = activity.elapsed_time;
          }
          
          // TAT calculation is already handled in dedicated hooks/components.
          // To avoid hammering the backend (many RPC calls per second), we skip
          // per-task calculate_task_tat here and leave TAT fields undefined.
          const tatData: any = null;
          
          const assignedToProfile = activity.assigned_to_profile;
          const assignedToDisplay = assignedToProfile?.full_name ?? activity.assigned_to ?? activity.doctor ?? 'Unassigned';
          const completedByDisplay = completedByProfile?.full_name ?? activity.completed_by ?? undefined;
          
          return {
            id: activity.id,
            taskName: activity.name,
            activityId: activity.activity_id,
            patientName: patient.name,
            patientId: patient.patient_id,
            patientUuid: patient.id,
            ward: patient.ward_name || 'N/A',
            bedRoom: patient.room && patient.bed ? `${patient.room}-${patient.bed}` : 'N/A',
            assignedTo: assignedToDisplay,
            completedBy: completedByDisplay,
            completionTime: activity.completion_time,
            timeTakenMinutes: activity.time_taken_minutes,
            status: activity.status as "pending" | "in-progress" | "completed" | "skipped",
            timestamp: activity.timestamp,
            elapsedTime: elapsedTime,
            elapsedMinutes: parseElapsedTime(elapsedTime),
            workflowStep: activity.workflow_step,
            previousStep: activity.previous_step,
            displayOrder: activity.display_order || 0,
            // Enhanced staff tracking
            assignedToStaff: assignedToProfile ? {
              id: assignedToProfile.id,
              fullName: assignedToProfile.full_name || '',
              email: assignedToProfile.email,
              phone: assignedToProfile.phone_number,
              role: assignedToProfile.role,
              department: assignedToProfile.department,
              designation: assignedToProfile.designation,
            } : undefined,
            completedByStaff: completedByProfile ? {
              id: completedByProfile.id,
              fullName: completedByProfile.full_name || '',
              email: completedByProfile.email,
              phone: completedByProfile.phone_number,
              role: completedByProfile.role,
              department: completedByProfile.department,
              designation: completedByProfile.designation,
            } : undefined,
            assignedAt: activity.assigned_at,
            startedAt: activity.started_at,
            // TAT fields
            allowedTatMinutes: tatData?.allowed_tat_minutes,
            level1ThresholdMinutes: tatData?.level_1_threshold_minutes,
            level2ThresholdMinutes: tatData?.level_2_threshold_minutes,
            level1RemainingMinutes: tatData?.level_1_remaining_minutes,
            level2RemainingMinutes: tatData?.level_2_remaining_minutes,
            gracePeriodMinutes: tatData?.grace_period_minutes,
            tatStatus: tatData?.tat_status,
            isDelayed: tatData?.is_delayed || false,
            patientTotalElapsedTime: patient.elapsed_time || '00:00:00',
            patientAdmissionDateTime: patient.admission_datetime,
          };
        })
      );
      
      // Identify discharged patients (where PD activity is completed)
      const dischargedPatientIds = new Set(
        tasks
          .filter(task => task.workflowStep === 'PD' && task.status === 'completed')
          .map(task => task.patientUuid)
      );
      
      // Filter out all tasks from discharged patients
      return tasks.filter(task => !dischargedPatientIds.has(task.patientUuid));
    },
    enabled: currentBranchId != null && !!organizationId,
    refetchInterval: 1000, // Refetch every second to update elapsed time
  });
};
